import React, { useMemo, useRef } from "react"

import { noop } from "shared/lib/noop"
import { getObjectProperty } from "shared/lib/objectUtils"
import { useEvent } from "shared/lib/useEvent"
import { RJSFFormData } from "shared/types/RJSF"

import { RJSFFormAPI } from "./RJSFForm"

interface RJSFFormContextType {
  registerForm: (id: string, api: RJSFFormAPI) => void
  resetAll: (defaultValues?: RJSFFormData) => void
}

export const RJSFFormContext = React.createContext<RJSFFormContextType>({
  registerForm: noop,
  resetAll: noop
})

export function RJSFFormContextProvider({
  children
}: {
  children: React.ReactNode
}) {
  const formApisRef = useRef<Record<string, RJSFFormAPI>>({})

  const handleRegister = useEvent((name: string, api: RJSFFormAPI) => {
    formApisRef.current = { ...formApisRef.current, [name]: api }
  })

  const handleResetAll = useEvent((defaultValues?: RJSFFormData) => {
    Object.entries(formApisRef.current).forEach(([name, formAPI]) =>
      formAPI.reset(
        defaultValues ? getObjectProperty(defaultValues, name) : undefined
      )
    )
  })

  const context = useMemo<RJSFFormContextType>(
    () => ({
      registerForm: handleRegister,
      resetAll: handleResetAll
    }),
    [handleRegister, handleResetAll]
  )

  return (
    <RJSFFormContext.Provider value={context}>
      {children}
    </RJSFFormContext.Provider>
  )
}
